import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OpportunityDocuments = ({ opportunityId, opportunityName, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    total: 0,
    devis: 0,
    bon_commande: 0,
    facture: 0,
    total_value: 0
  });

  useEffect(() => {
    if (opportunityId) {
      fetchDocuments();
    }
  }, [opportunityId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // Fetch all documents and filter by opportunity
      const response = await axios.get(`${API}/documents`);
      const opportunityDocuments = response.data.filter(doc => doc.opportunite_id === opportunityId);
      
      setDocuments(opportunityDocuments);
      
      // Calculate statistics
      const stats = {
        total: opportunityDocuments.length,
        devis: opportunityDocuments.filter(d => d.type === 'devis').length,
        bon_commande: opportunityDocuments.filter(d => d.type === 'bon_commande').length,
        facture: opportunityDocuments.filter(d => d.type === 'facture').length,
        total_value: opportunityDocuments.reduce((sum, doc) => sum + (doc.total_ttc || 0), 0)
      };
      
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (documentId, reference) => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${reference}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'devis': 'Devis',
      'bon_commande': 'Bon de commande',
      'facture': 'Facture'
    };
    return labels[type] || type;
  };

  const getDocumentTypeColor = (type) => {
    const colors = {
      'devis': 'bg-blue-100 text-blue-800',
      'bon_commande': 'bg-green-100 text-green-800',
      'facture': 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p>Chargement des documents...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                📋 Documents de l'opportunité
              </h2>
              <p className="text-gray-600 mt-1">{opportunityName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{statistics.total}</div>
              <div className="text-sm text-blue-800">Total Documents</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{statistics.devis}</div>
              <div className="text-sm text-green-800">Devis</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{statistics.bon_commande}</div>
              <div className="text-sm text-yellow-800">Bons de commande</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">{statistics.facture}</div>
              <div className="text-sm text-orange-800">Factures</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {statistics.total_value.toLocaleString()}
              </div>
              <div className="text-sm text-purple-800">Total TTC (FCFA)</div>
            </div>
          </div>

          {/* Pipeline de vente */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">🔄 Pipeline de vente</h3>
            <div className="flex items-center justify-center space-x-4">
              <div className={`px-4 py-2 rounded-lg ${statistics.devis > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                📝 Devis ({statistics.devis})
              </div>
              <div className="text-gray-400">→</div>
              <div className={`px-4 py-2 rounded-lg ${statistics.bon_commande > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                📋 Commandes ({statistics.bon_commande})
              </div>
              <div className="text-gray-400">→</div>
              <div className={`px-4 py-2 rounded-lg ${statistics.facture > 0 ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'}`}>
                🧾 Factures ({statistics.facture})
              </div>
            </div>
          </div>

          {/* Liste des documents */}
          {documents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-baobab-primary text-white">
                    <th className="border border-gray-300 p-3 text-left">Référence</th>
                    <th className="border border-gray-300 p-3 text-left">Type</th>
                    <th className="border border-gray-300 p-3 text-left">Date</th>
                    <th className="border border-gray-300 p-3 text-left">Total TTC</th>
                    <th className="border border-gray-300 p-3 text-left">Statut</th>
                    <th className="border border-gray-300 p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-baobab-light">
                      <td className="border border-gray-300 p-3 font-medium">{doc.reference}</td>
                      <td className="border border-gray-300 p-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDocumentTypeColor(doc.type)}`}>
                          {getDocumentTypeLabel(doc.type)}
                        </span>
                      </td>
                      <td className="border border-gray-300 p-3">
                        {new Date(doc.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="border border-gray-300 p-3 font-medium text-baobab-primary">
                        {doc.total_ttc.toLocaleString()} FCFA
                      </td>
                      <td className="border border-gray-300 p-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          doc.status === 'envoyé' ? 'bg-green-100 text-green-800' :
                          doc.status === 'brouillon' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {doc.status || 'brouillon'}
                        </span>
                      </td>
                      <td className="border border-gray-300 p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => downloadPDF(doc.id, doc.reference)}
                            className="bg-baobab-primary text-white px-3 py-1 rounded text-sm hover:bg-baobab-dark transition-colors"
                            title="Télécharger PDF"
                          >
                            📄 PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-4">📄</div>
              <h3 className="text-lg font-medium mb-2">Aucun document trouvé</h3>
              <p className="text-sm">Cette opportunité n'a pas encore de documents associés.</p>
              <p className="text-sm mt-1">Créez un devis pour commencer le processus commercial.</p>
            </div>
          )}

          {/* Bouton pour créer un nouveau document */}
          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="bg-baobab-primary text-white px-6 py-3 rounded-lg hover:bg-baobab-dark transition-colors"
            >
              ➕ Créer un nouveau document pour cette opportunité
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDocuments;